import { NumberFormatter, NumberParser } from '@internationalized/number';
import { LanguageElements } from '../../language-elements/LanguageElements';
import { Formats } from '@smartsheet/core-views-api-models';

export interface CurrencyValue {
    currency: Formats.Currency;
    value: number;
    formattedValue: string;
    currencySymbol: string;
    isCurrencySymbolLeft: boolean;
}

export const currencyCountryBySymbol = new Map<string, Formats.Currency>([
    ['en-US$', Formats.Currency.USD],
    ['sk-SK$', Formats.Currency.USD],
    ['es-AR$', Formats.Currency.ARS],
    ['en-AU$', Formats.Currency.AUD],
    ['en-CA$', Formats.Currency.CAD],
    ['zh-HK$', Formats.Currency.HKD],
    ['es-MX$', Formats.Currency.MXN],
    ['en-NZ$', Formats.Currency.NZD],
    ['es-CL$', Formats.Currency.CLP],
    ['$', Formats.Currency.USD],
    ['US$', Formats.Currency.USD],
    ['AR$', Formats.Currency.ARS],
    ['A$', Formats.Currency.AUD],
    ['AU$', Formats.Currency.AUD],
    ['R$', Formats.Currency.BRL],
    ['CA$', Formats.Currency.CAD],
    ['CLP$', Formats.Currency.CLP],
    ['CN¥', Formats.Currency.CNY],
    ['kr.', Formats.Currency.DKK],
    ['€', Formats.Currency.EUR],
    ['HK$', Formats.Currency.HKD],
    ['₹', Formats.Currency.INR],
    ['₪', Formats.Currency.ILS],
    ['¥', Formats.Currency.JPY],
    ['MX$', Formats.Currency.MXN],
    ['NZ$', Formats.Currency.NZD],
    ['₽', Formats.Currency.RUB],
    ['SG$', Formats.Currency.SGD],
    ['₩', Formats.Currency.KRW],
    ['£', Formats.Currency.GBP],
    ['R', Formats.Currency.ZAR],
    ['SFr', Formats.Currency.CHF],
    ['CHF', Formats.Currency.CHF],
    ['FSK', Formats.Currency.CHF],
    ['Fr', Formats.Currency.CHF],
]);

export const currencyCodeByCountry = new Map<Formats.Currency, string>([
    [Formats.Currency.MXN, 'MXN'],
    [Formats.Currency.ARS, 'ARS'],
    [Formats.Currency.AUD, 'AUD'],
    [Formats.Currency.BRL, 'BRL'],
    [Formats.Currency.CAD, 'CAD'],
    [Formats.Currency.CHF, 'CHF'],
    [Formats.Currency.CLP, 'CLP'],
    [Formats.Currency.CNY, 'CNY'],
    [Formats.Currency.DKK, 'DKK'],
    [Formats.Currency.EUR, 'EUR'],
    [Formats.Currency.GBP, 'GBP'],
    [Formats.Currency.HKD, 'HKD'],
    [Formats.Currency.ILS, 'ILS'],
    [Formats.Currency.NZD, 'NZD'],
    [Formats.Currency.INR, 'INR'],
    [Formats.Currency.JPY, 'JPY'],
    [Formats.Currency.KRW, 'KRW'],
    [Formats.Currency.NOK, 'NOK'],
    [Formats.Currency.RUB, 'RUB'],
    [Formats.Currency.SEK, 'SEK'],
    [Formats.Currency.SGD, 'SGD'],
    [Formats.Currency.TWD, 'TWD'],
    [Formats.Currency.USD, 'USD'],
    [Formats.Currency.ZAR, 'ZAR'],
]);

const extraLeadingZeroRegex = /^0\d/;
const validCurrencySymbols = 'kr\\.|CN¥|¥|€|£|₪|₹|₩|₽|A\\$|AR\\$|AU\\$|CA\\$|CLP\\$|HK\\$|MX\\$|NZ\\$|R\\$|SG\\$|US\\$|\\$|\\R';
const numberGroup = `[0-9.,\\-]`;

/**
 * Parses a localized currency string and extracts its numeric value, symbol, and currency metadata.
 */
export const parseCurrencyForTextNumber = (value: string, locale: string, decimalCount: number = 2): CurrencyValue | undefined => {
    if (value.startsWith('R') && !/^R\s?\d/.test(value)) {
        return;
    }

    const normalizedInput = value.trim();
    let isNegative = false;

    if (/^- ?/.test(normalizedInput)) {
        isNegative = true;
        value = normalizedInput.replace(/^- ?/, '');
    }

    const currencyRegex = new RegExp(`^(${validCurrencySymbols})*\\s*(${numberGroup}+)\\s*(${validCurrencySymbols}$)*\\s*(.+)*`);
    const currencyValue = currencyRegex.exec(value);
    const shouldNotBeParsedAsCurrency = currencyValue?.[4] !== undefined;

    if (!currencyValue || shouldNotBeParsedAsCurrency) {
        return;
    }

    const currencySymbol = currencyValue[1] || currencyValue[3];
    const amount = currencyValue[2];
    if (extraLeadingZeroRegex.test(amount)) {
        return;
    }

    const currency = currencyCountryBySymbol.get(currencySymbol ?? '') ?? Formats.Currency.NONE;
    const currencyCode = currencyCodeByCountry.get(currency);
    if (!currencyCode) {
        return;
    }

    const formatter = new NumberFormatter(locale, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: decimalCount,
        minimumFractionDigits: decimalCount,
    });
    const parser = new NumberParser(locale, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 18,
    });

    const cleanAmount = amount.replace(/,/g, '');
    const numericAmount = Number(cleanAmount);
    if (isNaN(numericAmount)) {
        return;
    }

    try {
        const finalValue = isNegative ? -numericAmount : numericAmount;
        const formatted = formatter.format(finalValue);
        const numeric = parser.parse(formatted);

        if (isNaN(numeric)) {
            return;
        }

        const formattedValue = formatCurrency(numeric, formatter);
        const isCurrencySymbolLeft = getCurrencySymbolPosition(formatted, currencyCode, locale);

        return {
            currency,
            value: numeric,
            formattedValue,
            currencySymbol: currencySymbol ?? '',
            isCurrencySymbolLeft,
        };
    } catch (error) {
        const fallbackFormatted = `${currencySymbol ?? ''}${numericAmount.toLocaleString(locale, {
            minimumFractionDigits: decimalCount,
            maximumFractionDigits: decimalCount,
        })}`;

        return {
            currency,
            value: isNegative ? -numericAmount : numericAmount,
            formattedValue: fallbackFormatted,
            currencySymbol: currencySymbol ?? '',
            isCurrencySymbolLeft: true,
        };
    }
};
/**
 * Formats a numeric value as a localized currency string using the given formatter.
 */
const formatCurrency = (value: number, formatter: NumberFormatter): string => {
    return formatter.format(value);
};

function getCurrencySymbolPosition(formatted: string, currencyCode: string, locale: string): boolean {
    const parts = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
    }).formatToParts(1);

    const currencySymbol = parts.find((p) => p.type === 'currency')?.value ?? '';
    return formatted.trim().startsWith(currencySymbol);
}

export const generateCurrencyNameMap = (languageElements: LanguageElements): Map<Formats.Currency, string> => {
    return new Map([
        [Formats.Currency.NONE, ''],
        [Formats.Currency.ARS, languageElements.CURRENCY_ARGENTINA_PESO],
        [Formats.Currency.AUD, languageElements.CURRENCY_AUSTRALIA_DOLLAR],
        [Formats.Currency.BRL, languageElements.CURRENCY_BRAZIL_REAL],
        [Formats.Currency.CAD, languageElements.CURRENCY_CANADA_DOLLAR],
        [Formats.Currency.CHF, languageElements.CURRENCY_SWITZERLAND_FRANC],
        [Formats.Currency.CLP, languageElements.CURRENCY_CHILE_PESO],
        [Formats.Currency.CNY, languageElements.CURRENCY_CHINA_YUAN],
        [Formats.Currency.DKK, languageElements.CURRENCY_DENMARK_KRONE],
        [Formats.Currency.EUR, languageElements.CURRENCY_EURO],
        [Formats.Currency.GBP, languageElements.CURRENCY_UNITED_KINGDOM_POUND],
        [Formats.Currency.HKD, languageElements.CURRENCY_HONG_KONG_DOLLAR],
        [Formats.Currency.ILS, languageElements.CURRENCY_ISRAEL_NEW_SHEKEL],
        [Formats.Currency.INR, languageElements.CURRENCY_INDIA_RUPEE],
        [Formats.Currency.JPY, languageElements.CURRENCY_JAPAN_YEN],
        [Formats.Currency.KRW, languageElements.CURRENCY_SOUTH_KOREA_WON],
        [Formats.Currency.MXN, languageElements.CURRENCY_MEXICO_PESO],
        [Formats.Currency.NOK, languageElements.CURRENCY_NORWAY_KRONE],
        [Formats.Currency.NZD, languageElements.CURRENCY_NEW_ZEALAND_DOLLAR],
        [Formats.Currency.RUB, languageElements.CURRENCY_RUSSIA_RUBLE],
        [Formats.Currency.SEK, languageElements.CURRENCY_SWEDEN_KRONA],
        [Formats.Currency.SGD, languageElements.CURRENCY_SINGAPORE_DOLLAR],
        [Formats.Currency.USD, languageElements.CURRENCY_UNITED_STATES_DOLLAR],
        [Formats.Currency.ZAR, languageElements.CURRENCY_SOUTH_AFRICA_RAND],
    ]);
};
